import classNames from "classnames";
import React, {
  ChangeEventHandler,
  ClipboardEventHandler,
  FocusEventHandler,
  FormEventHandler,
  forwardRef,
  KeyboardEventHandler,
} from "react";

import { FieldLabel } from "../newUI/text";

type SettingsInputSize = "sm" | "md" | "lg";
type SettingsInputFocusStyle = "shadow" | "select";

const FORM_INPUT_SIZE_CLASSES: Record<SettingsInputSize, string> = {
  sm: "h-8 py-1.5 text-sm",
  md: "h-9 py-2 text-sm",
  lg: "h-10 py-2.5 text-base",
};

type Props = {
  name?: string;
  label?: string;
  required?: boolean;
  placeholder?: string;
  value?: string;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  onInput?: FormEventHandler<HTMLInputElement>;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
  onPaste?: ClipboardEventHandler<HTMLInputElement>;
  inputClasses?: string;
  containerClasses?: string;
  labelClasses?: string;
  isError?: boolean;
  type?: React.HTMLInputTypeAttribute;
  min?: number | string;
  max?: number | string;
  step?: number | string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
  maxLength?: number;
  variant?: "settings" | "form";
  focusStyle?: SettingsInputFocusStyle;
  size?: SettingsInputSize;
};

export const SettingsInput = forwardRef<HTMLInputElement, Props>(
  (
    {
      name,
      label,
      required,
      placeholder,
      value,
      onChange,
      onBlur,
      onInput,
      onKeyDown,
      onPaste,
      inputClasses,
      containerClasses,
      labelClasses,
      isError,
      type = "text",
      min,
      max,
      step,
      inputMode,
      maxLength,
      variant = "settings",
      focusStyle = "shadow",
      size = "md",
    },
    ref,
  ) => {
    const inputSizeClasses =
      variant === "form"
        ? FORM_INPUT_SIZE_CLASSES[size]
        : size === "sm"
        ? "h-8 py-1.5 text-sm"
        : "h-10 py-2.5 text-sm";

    return (
      <div className={classNames("", containerClasses)}>
        {label && (
          <FieldLabel
            label={label}
            required={required}
            className={classNames(
              "mb-1",
              variant === "form" ? "text-[#253747]" : "text-[#5F758F]",
              labelClasses,
            )}
          />
        )}
        <input
          ref={ref}
          name={name}
          className={classNames(
            "rounded-lg border px-3 font-medium text-[#1D2C3B] outline-none transition-all duration-300 placeholder:font-medium placeholder:text-[#A1AFC0]",
            inputSizeClasses,
            focusStyle === "select"
              ? isError
                ? "border-[#DC2626] focus:border-[#DC2626]"
                : "border-[#1f293733] focus:border-[#3e4bff] focus:[box-shadow:0_0_0_1px_#3e4bff]"
              : isError
              ? "border-[#DC2626] focus:shadow-settings-error"
              : "border-[#C8D2DE] focus:border-[#6938EF] focus:shadow-settings-purple",
            inputClasses,
          )}
          type={type}
          min={min}
          max={max}
          step={step}
          inputMode={inputMode}
          maxLength={maxLength}
          placeholder={placeholder}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          onInput={onInput}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
        />
      </div>
    );
  },
);

SettingsInput.displayName = "SettingsInput";
